// Function index:
//   tweenPosition(ticker, target, toX, toY, ms?) — ease a container to a point.
//   tweenAlpha(ticker, target, toAlpha, ms?)     — fade a display object.
//   cancelTween(target)                          — stop an in-flight tween.
//
// Tiny ticker-driven tween helper for the hex scene. No dependency beyond the
// shared app.ticker. A per-object WeakMap of cancel handles means starting a
// new tween on the same object supersedes the previous one, and lets the tile
// culling pool stop a tween before recycling the Graphics it was animating
// (otherwise a recycled tile would keep ticking on the wrong target).

import type { Container, Ticker } from 'pixi.js';

// Keyed by the animated object; value cancels the in-flight tween.
const active = new WeakMap<object, () => void>();

export function cancelTween(target: object): void {
  active.get(target)?.();
}

function easeOutCubic(x: number): number {
  return 1 - Math.pow(1 - x, 3);
}

export function tweenPosition(
  ticker: Ticker,
  target: Container,
  toX: number,
  toY: number,
  durationMs = 200,
): void {
  cancelTween(target);
  const fromX = target.x;
  const fromY = target.y;
  if (fromX === toX && fromY === toY) return;
  let elapsed = 0;
  const step = (tk: Ticker): void => {
    elapsed += tk.deltaMS;
    const k = Math.min(1, elapsed / durationMs);
    const e = easeOutCubic(k);
    target.x = fromX + (toX - fromX) * e;
    target.y = fromY + (toY - fromY) * e;
    if (k >= 1) { ticker.remove(step); active.delete(target); }
  };
  active.set(target, () => {
    ticker.remove(step);
    active.delete(target);
    target.x = toX; target.y = toY;
  });
  ticker.add(step);
}

export function tweenAlpha(
  ticker: Ticker,
  target: Container,
  toAlpha: number,
  durationMs = 200,
): void {
  cancelTween(target);
  const fromAlpha = target.alpha;
  if (fromAlpha === toAlpha) return;
  let elapsed = 0;
  const step = (tk: Ticker): void => {
    elapsed += tk.deltaMS;
    const k = Math.min(1, elapsed / durationMs);
    target.alpha = fromAlpha + (toAlpha - fromAlpha) * k;
    if (k >= 1) { ticker.remove(step); active.delete(target); }
  };
  active.set(target, () => {
    ticker.remove(step);
    active.delete(target);
    target.alpha = toAlpha;
  });
  ticker.add(step);
}
